import React from 'react';

import { Empty, List, Pagination, Spin, Typography } from 'antd';
import styled from 'styled-components';

import { TABLE_PAGE_SIZE_OPTIONS } from './constants';
import IncentiveScrappingListHeader from './IncentiveScrappingListHeader';
import IncentiveScrappingListItem from './IncentiveScrappingListItem';
import { CompanyPreferenceItem } from './types';

const ListSection = styled.div`
  padding: 0;
  width: 100%;
`;

const StyledList = styled(List)`
  && .ant-list-item:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  && .ant-list-item:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const StyledPagination = styled(Pagination)`
  &&.ant-pagination {
    font-size: 12px;

    .ant-pagination-item {
      line-height: 24px;
      color: var(--text-primary);
      border-radius: 32px;
      line-height: 32px;
    }

    .ant-pagination-item-active {
      border-radius: 32px;
      box-shadow: 0 2px 6px 0 var(--box-shadow-gray);
      background-color: #fff;
      border: none;
      a {
        color: var(--text-primary) !important;
      }
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  padding: 21px 15px 0 4px;
`;

const PaginationText = styled(Typography.Text)`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

type IncentiveScrappingCompanyListProps = {
  isMultipleSelection: boolean;
  allVisibleSelected: boolean;
  onToggleAllVisible: () => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  paginatedItems: CompanyPreferenceItem[];
  filteredItemsCount: number;
  selectedCompanyIds: number[];
  updatingCompanyIds: number[];
  onToggleOne: (companyId: number) => void;
  onTogglePreference: (companyId: number, enabled: boolean) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const IncentiveScrappingCompanyList = ({
  isMultipleSelection,
  allVisibleSelected,
  onToggleAllVisible,
  isLoading,
  isFetching,
  isError,
  paginatedItems,
  filteredItemsCount,
  selectedCompanyIds,
  updatingCompanyIds,
  onToggleOne,
  onTogglePreference,
  page,
  pageSize,
  setPage,
  setPageSize,
}: IncentiveScrappingCompanyListProps) => {
  return (
    <ListSection>
      <IncentiveScrappingListHeader
        isMultipleSelection={isMultipleSelection}
        allVisibleSelected={allVisibleSelected}
        onToggleAllVisible={onToggleAllVisible}
      />

      {isLoading || isFetching ? (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
          <Spin />
        </div>
      ) : isError ? (
        <div style={{ padding: 24 }}>
          <Empty description="Failed to load companies" />
        </div>
      ) : (
        <>
          <StyledList
            bordered
            dataSource={paginatedItems}
            locale={{ emptyText: <Empty description="No companies found" /> }}
            renderItem={(item: any) => (
              <IncentiveScrappingListItem
                item={item}
                isMultipleSelection={isMultipleSelection}
                isSelected={selectedCompanyIds.includes(item.company)}
                isUpdating={updatingCompanyIds.includes(item.company)}
                onToggleOne={onToggleOne}
                onTogglePreference={onTogglePreference}
              />
            )}
          />
          <PaginationContainer>
            <StyledPagination
              total={filteredItemsCount}
              current={page}
              pageSize={pageSize}
              pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
              onChange={(nextPage) => setPage(nextPage)}
              onShowSizeChange={(_current, nextPageSize) => {
                setPage(1);
                setPageSize(nextPageSize);
              }}
            />
            <PaginationText>
              Showing {paginatedItems.length} of {filteredItemsCount} records
            </PaginationText>
          </PaginationContainer>
        </>
      )}
    </ListSection>
  );
};

export default IncentiveScrappingCompanyList;
